import React, { useState, useEffect, useRef } from 'react';
import { 
 Play, 
 Pause,
 Download, 
 FileText,
 Music,
 Youtube,
 ArrowRight,
 CheckCircle,
 Loader2,
 AlertCircle,
 Volume2,
 Edit3,
 Save,
 X,
 SkipBack,
 SkipForward,
 AudioLines,
 Clock,
 User,
 Settings
} from 'lucide-react';
import youtubeAPI from '../api/youtubeApi';

export default function PodcastPage() {
 
 // 主要状态管理
 const [currentStep, setCurrentStep] = useState(1); // 1: 输入URL, 2: 下载视频, 3: 转录配置, 4: 转录处理, 5: 编辑器, 6: 导出中, 7: 完成
 const [youtubeUrl, setYoutubeUrl] = useState('');
 const [processing, setProcessing] = useState(false);
 const [error, setError] = useState('');
 const [exportProgress, setExportProgress] = useState(0);
 const [jobId, setJobId] = useState(null);
 const [downloadUrl, setDownloadUrl] = useState(null);
 const [transcribeJobId, setTranscribeJobId] = useState(null);
 
 // 转录配置状态
 const [transcriptionLanguage, setTranscriptionLanguage] = useState('chinese');
 const [transcriptionFormat, setTranscriptionFormat] = useState('json');
 const [transcriptionResult, setTranscriptionResult] = useState(null);
 const [showTranscriptionDetail, setShowTranscriptionDetail] = useState(false);
 
 // 文本切片配置 - 默认使用原始分段
 const [sliceMode, setSliceMode] = useState('original'); // 优先使用转录API的原始片段
 const [sliceSettings, setSliceSettings] = useState({
 timeInterval: 30, // 按时间切片：每30秒一段
 sentenceCount: 3, // 按句子切片：每3句话一段
 customBreaks: [] // 自定义切分点
 });
 const [showSliceConfig, setShowSliceConfig] = useState(false);
 
 // 音频切片状态
 const [audioSlicingTaskId, setAudioSlicingTaskId] = useState(null);
 const [audioSlicingStatus, setAudioSlicingStatus] = useState(null); // pending, processing, completed, failed
 const [audioSlicingProgress, setAudioSlicingProgress] = useState(0);
 const [showAudioSlicing, setShowAudioSlicing] = useState(false);
 
 // 音频和文本数据
 const [audioData, setAudioData] = useState(null);
 const [textSegments, setTextSegments] = useState([]);
 const [currentTime, setCurrentTime] = useState(0);
 const [duration, setDuration] = useState(0);
 const [isPlaying, setIsPlaying] = useState(false);
 
 // 编辑状态
 const [selectedSegment, setSelectedSegment] = useState(null);
 const [editingSegment, setEditingSegment] = useState(null);
 const [newText, setNewText] = useState('');
 const [selectedVoice, setSelectedVoice] = useState('female');
 const [regenerating, setRegenerating] = useState(false);
 
 // 音频引用
 const audioRef = useRef(null);
 const timelineRef = useRef(null);
 const playbackIntervalRef = useRef(null);

 // 模拟的文本片段数据（实际应该从API获取）
 const mockSegments = [
 {
 id: 1,
 startTime: 0,
 endTime: 4.5,
 text: "欢迎大家来到今天的节目，我是主播小明。",
 audioUrl: "/mock/segment1.mp3",
 isModified: false
 },
 {
 id: 2,
 startTime: 4.5,
 endTime: 9.8,
 text: "今天我们要聊的话题是人工智能的发展趋势。",
 audioUrl: "/mock/segment2.mp3",
 isModified: false
 },
 {
 id: 3,
 startTime: 9.8,
 endTime: 15.2,
 text: "近年来，AI技术在各个领域都有突破性进展。",
 audioUrl: "/mock/segment3.mp3",
 isModified: false
 },
 {
 id: 4,
 startTime: 15.2,
 endTime: 22.1,
 text: "从自然语言处理到计算机视觉，AI正在改变我们的生活方式。",
 audioUrl: "/mock/segment4.mp3",
 isModified: false
 },
 {
 id: 5,
 startTime: 22.1,
 endTime: 28.5,
 text: "让我们一起探讨这些技术背后的原理和未来的可能性。",
 audioUrl: "/mock/segment5.mp3",
 isModified: false
 }
 ];

 // 检查YouTube URL格式
 const isValidYoutubeUrl = (url) => {
 return youtubeAPI.validateYouTubeUrl(url);
 };

 // 处理YouTube视频
 const handleProcessVideo = async () => {
 if (!isValidYoutubeUrl(youtubeUrl)) {
 setError('请输入有效的YouTube链接');
 return;
 }

 setProcessing(true);
 setError('');
 setCurrentStep(2);
 setProcessingSteps({
 download: 'processing',
 extract: 'pending',
 transcribe: 'pending'
 });

 try {
 // 第一步：提交下载任务
 setProcessingSteps({
 download: 'processing',
 extract: 'pending',
 transcribe: 'pending'
 });

 const submitResult = await youtubeAPI.submitDownload(youtubeUrl);
 
 if (!submitResult.success) {
 throw new Error(submitResult.message || '提交任务失败');
 }

 setJobId(submitResult.job_id);
 
 // 第二步：轮询任务状态
 setProcessingSteps({
 download: 'completed',
 extract: 'processing',
 transcribe: 'pending'
 });

 const finalResult = await youtubeAPI.pollJobStatus(
 submitResult.job_id,
 (statusResult) => {
 // 根据任务状态更新UI
 if (statusResult.status === 'processing') {
 setProcessingSteps({
 download: 'completed',
 extract: 'processing',
 transcribe: 'pending'
 });
 }
 }
 );

 // 第三步：处理完成
 setProcessingSteps({
 download: 'completed',
 extract: 'completed',
 transcribe: 'completed'
 });

 // 提取下载URL
 const videoDownloadUrl = youtubeAPI.extractDownloadUrl(finalResult);
 if (!videoDownloadUrl) {
 throw new Error('无法获取下载链接');
 }

 setDownloadUrl(videoDownloadUrl);
 
 // 步骤3：进入转录配置
 setCurrentStep(3);
 setProcessing(false);
 
 } catch (err) {
 console.error('YouTube下载处理失败:', err);
 setError(err.message || '处理失败，请重试');
 setCurrentStep(1);
 } finally {
 setProcessing(false);
 }
 };

 // 文本切片算法
 const sliceTextSegments = (originalSegments, mode, settings) => {
 if (mode === 'original') {
 return originalSegments;
 }
 
 let slicedSegments = [];
 
 switch (mode) {
 case 'time':
 // 按时间间隔切片
 const timeInterval = settings.timeInterval;
 let currentSlice = [];
 let currentStart = 0;
 let sliceId = 1;
 
 originalSegments.forEach(segment => {
 if (segment.startTime >= currentStart + timeInterval) {
 if (currentSlice.length > 0) {
 slicedSegments.push({
 id: sliceId++,
 startTime: currentSlice[0].startTime,
 endTime: currentSlice[currentSlice.length - 1].endTime,
 text: currentSlice.map(s => s.text).join(' '),
 originalSegments: currentSlice,
 isModified: false
 });
 }
 currentSlice = [segment];
 currentStart = segment.startTime;
 } else {
 currentSlice.push(segment);
 }
 });
 
 // 处理最后一个切片
 if (currentSlice.length > 0) {
 slicedSegments.push({
 id: sliceId++,
 startTime: currentSlice[0].startTime,
 endTime: currentSlice[currentSlice.length - 1].endTime,
 text: currentSlice.map(s => s.text).join(' '),
 originalSegments: currentSlice,
 isModified: false
 });
 }
 break;
 
 case 'sentence':
 // 按句子数量切片
 const sentenceCount = settings.sentenceCount;
 let sentences = [];
 let currentSentenceSlice = [];
 let sentenceSliceId = 1;
 
 originalSegments.forEach(segment => {
 // 简单的句子分割（基于标点符号）
 const segmentSentences = segment.text.split(/[。！？.!?]+/).filter(s => s.trim());
 segmentSentences.forEach(sentence => {
 if (sentence.trim()) {
 sentences.push({
 text: sentence.trim(),
 segment: segment
 });
 }
 });
 });
 
 // 按句子数量分组
 for (let i = 0; i < sentences.length; i += sentenceCount) {
 const sentenceGroup = sentences.slice(i, i + sentenceCount);
 const firstSegment = sentenceGroup[0].segment;
 const lastSegment = sentenceGroup[sentenceGroup.length - 1].segment;
 
 slicedSegments.push({
 id: sentenceSliceId++,
 startTime: firstSegment.startTime,
 endTime: lastSegment.endTime,
 text: sentenceGroup.map(s => s.text).join('。'),
 originalSegments: sentenceGroup.map(s => s.segment),
 isModified: false
 });
 }
 break;
 
 case 'paragraph':
 // 按段落切片（基于较长的停顿）
 let paragraphSlice = [];
 let paragraphId = 1;
 
 originalSegments.forEach((segment, index) => {
 paragraphSlice.push(segment);
 
 // 如果是最后一个片段，或者下一个片段与当前片段有较长间隔（超过2秒）
 const nextSegment = originalSegments[index + 1];
 if (!nextSegment || (nextSegment.startTime - segment.endTime > 2)) {
 slicedSegments.push({
 id: paragraphId++,
 startTime: paragraphSlice[0].startTime,
 endTime: paragraphSlice[paragraphSlice.length - 1].endTime,
 text: paragraphSlice.map(s => s.text).join(' '),
 originalSegments: [...paragraphSlice],
 isModified: false
 });
 paragraphSlice = [];
 }
 });
 break;
 
 case 'custom':
 // 自定义切分点
 const customBreaks = settings.customBreaks.sort((a, b) => a - b);
 let customStart = 0;
 let customId = 1;
 
 customBreaks.forEach(breakPoint => {
 const segmentsInRange = originalSegments.filter(
 s => s.startTime >= customStart && s.endTime <= breakPoint
 );
 
 if (segmentsInRange.length > 0) {
 slicedSegments.push({
 id: customId++,
 startTime: segmentsInRange[0].startTime,
 endTime: segmentsInRange[segmentsInRange.length - 1].endTime,
 text: segmentsInRange.map(s => s.text).join(' '),
 originalSegments: segmentsInRange,
 isModified: false
 });
 }
 customStart = breakPoint;
 });
 
 // 处理最后一段
 const lastSegments = originalSegments.filter(s => s.startTime >= customStart);
 if (lastSegments.length > 0) {
 slicedSegments.push({
 id: customId++,
 startTime: lastSegments[0].startTime,
 endTime: lastSegments[lastSegments.length - 1].endTime,
 text: lastSegments.map(s => s.text).join(' '),
 originalSegments: lastSegments,
 isModified: false
 });
 }
 break;
 
 default:
 return originalSegments;
 }
 
 return slicedSegments;
 };

 // 处理转录配置
 const handleTranscriptionConfig = async () => {
 if (!downloadUrl) {
 setError('视频下载链接不存在，请重新下载');
 return;
 }

 setProcessing(true);
 setError('');
 setCurrentStep(4); // 进入转录处理步骤
 
 try {
 // 使用 Lemonfox AI 进行转录
 console.log('开始使用 Lemonfox AI 转录视频...');
 const transcriptionResult = await youtubeAPI.transcribeWithLemonfox(
 downloadUrl, 
 transcriptionLanguage, 
 transcriptionFormat
 );
 
 // 保存转录结果
 setTranscriptionResult(transcriptionResult);
 
 // 打印结果到控制台
 console.log('=== Lemonfox AI 转录完成 ===');
 console.log('语言:', transcriptionLanguage);
 console.log('格式:', transcriptionFormat);
 if (typeof transcriptionResult === 'string') {
 console.log('结果:', transcriptionResult);
 } else {
 console.log(JSON.stringify(transcriptionResult, null, 2));
 }
 console.log('========================');
 
 // 处理转录结果
 let processedSegments = [];
 let audioDuration = 0;
 
 if (transcriptionFormat === 'verbose_json' && transcriptionResult.segments) {
 // 处理verbose_json格式的数据
 audioDuration = transcriptionResult.duration || 0;
 processedSegments = transcriptionResult.segments.map(segment => ({
 id: segment.id + 1,
 startTime: segment.start,
 endTime: segment.end,
 text: segment.text,
 isModified: false,
 avgLogprob: segment.avg_logprob,
 language: segment.language,
 speaker: segment.speaker
 }));
 } else if (transcriptionFormat === 'json' && transcriptionResult.segments) {
 // 处理普通json格式的数据
 processedSegments = transcriptionResult.segments.map((segment, index) => ({
 id: index + 1,
 startTime: segment.start || 0,
 endTime: segment.end || 0,
 text: segment.text || '',
 isModified: false
 }));
 audioDuration = Math.max(...processedSegments.map(s => s.endTime));
 } else {
 // 其他格式使用模拟数据
 processedSegments = mockSegments;
 audioDuration = 28.5;
 }
 
 // 应用文本切片算法
 const slicedSegments = sliceTextSegments(processedSegments, sliceMode, sliceSettings);
 setTextSegments(slicedSegments);
 setDuration(audioDuration);
 
 // 设置音频数据
 setAudioData({
 url: downloadUrl,
 duration: audioDuration,
 title: '从YouTube提取的音频'
 });
 
 // 完成转录，进入编辑器
 setCurrentStep(5);
 
 } catch (err) {
 console.error('转录失败:', err);
 setError('转录失败: ' + err.message);
 // 失败时也使用模拟数据
 setTextSegments(mockSegments);
 setDuration(28.5);
 setAudioData({
 url: downloadUrl,
 duration: 28.5,
 title: '从YouTube提取的音频'
 });
 setCurrentStep(5);
 } finally {
 setProcessing(false);
 }
 };

 // 格式化时间显示
 const formatTime = (seconds) => {
 const mins = Math.floor(seconds / 60);
 const secs = Math.floor(seconds % 60);
 return `${mins}:${secs.toString().padStart(2, '0')}`;
 };

 // 播放控制
 const togglePlay = () => {
 setIsPlaying(!isPlaying);
 if (audioRef.current) {
 if (isPlaying) {
 audioRef.current.pause();
 } else {
 // 如果没有音频源，使用模拟播放
 if (!audioRef.current.src) {
 simulatePlayback();
 } else {
 audioRef.current.play();
 }
 }
 } else {
 // 模拟播放
 simulatePlayback();
 }
 };

 const seekTo = (time) => {
 setCurrentTime(time);
 if (audioRef.current && audioRef.current.src) {
 audioRef.current.currentTime = time;
 }
 };

 // 模拟播放进度
 const simulatePlayback = () => {
 if (playbackIntervalRef.current) {
 clearInterval(playbackIntervalRef.current);
 }
 
 playbackIntervalRef.current = setInterval(() => {
 setCurrentTime(prev => {
 const newTime = prev + 0.1;
 if (newTime >= duration) {
 setIsPlaying(false);
 clearInterval(playbackIntervalRef.current);
 playbackIntervalRef.current = null;
 return duration;
 }
 return newTime;
 });
 }, 100);
 };

 // 停止模拟播放
 const stopSimulatePlayback = () => {
 if (playbackIntervalRef.current) {
 clearInterval(playbackIntervalRef.current);
 playbackIntervalRef.current = null;
 }
 };

 // 监听播放状态变化
 useEffect(() => {
 if (isPlaying && (!audioRef.current || !audioRef.current.src)) {
 simulatePlayback();
 } else if (!isPlaying) {
 stopSimulatePlayback();
 }
 
 // 清理函数
 return () => {
 stopSimulatePlayback();
 };
 }, [isPlaying, duration]);

 // 键盘快捷键
 useEffect(() => {
 const handleKeyPress = (e) => {
 // 如果正在编辑文本，不处理快捷键
 if (editingSegment || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
 return;
 }
 
 switch (e.code) {
 case 'Space':
 e.preventDefault();
 togglePlay();
 break;
 case 'ArrowLeft':
 e.preventDefault();
 seekTo(Math.max(0, currentTime - 10));
 break;
 case 'ArrowRight':
 e.preventDefault();
 seekTo(Math.min(duration, currentTime + 10));
 break;
 default:
 break;
 }
 };

 if (currentStep === 5) { // 只在编辑器界面启用快捷键
 window.addEventListener('keydown', handleKeyPress);
 return () => {
 window.removeEventListener('keydown', handleKeyPress);
 };
 }
 }, [currentStep, currentTime, duration, isPlaying, editingSegment]);

 // 选择文本片段
 const selectSegment = (segment) => {
 setSelectedSegment(segment);
 seekTo(segment.startTime);
 };

 // 播放指定片段
 const playSegment = (segment) => {
 // 如果有切片任务ID，使用切片的音频文件
 if (audioSlicingTaskId && audioSlicingStatus === 'completed') {
 playSlicedSegment(segment);
 } else {
 // 使用原始方式播放
 playOriginalSegment(segment);
 }
 };

 // 播放切片的音频段
 const playSlicedSegment = (segment) => {
 const segmentUrl = youtubeAPI.getSegmentUrl(audioSlicingTaskId, segment.id);
 
 // 创建新的audio元素播放切片
 const segmentAudio = new Audio(segmentUrl);
 segmentAudio.play();
 setIsPlaying(true);
 setSelectedSegment(segment);
 
 segmentAudio.onended = () => {
 setIsPlaying(false);
 };
 
 segmentAudio.onerror = (error) => {
 console.error('播放切片失败:', error);
 // 降级到原始播放方式
 playOriginalSegment(segment);
 };
 };

 // 播放原始音频段（时间控制）
 const playOriginalSegment = (segment) => {
 if (audioRef.current && downloadUrl) {
 // 设置音频源
 if (audioRef.current.src !== downloadUrl) {
 audioRef.current.src = downloadUrl;
 }
 
 // 跳转到片段开始时间
 audioRef.current.currentTime = segment.startTime;
 
 // 播放音频
 audioRef.current.play();
 setIsPlaying(true);
 
 // 设置定时器，在片段结束时暂停
 const duration = segment.endTime - segment.startTime;
 setTimeout(() => {
 if (audioRef.current && !audioRef.current.paused) {
 audioRef.current.pause();
 setIsPlaying(false);
 }
 }, duration * 1000);
 
 setSelectedSegment(segment);
 } else {
 // 降级到模拟播放
 selectSegment(segment);
 simulatePlayback();
 }
 };

 // 开始音频切片
 const startAudioSlicing = async () => {
 if (!downloadUrl || !textSegments.length) {
 setError('缺少音频文件或文本片段');
 return;
 }

 setShowAudioSlicing(true);
 setAudioSlicingStatus('pending');
 setAudioSlicingProgress(0);

 try {
 // 提交切片任务
 const result = await youtubeAPI.submitAudioSlicing(downloadUrl, textSegments);
 
 if (result.success) {
 setAudioSlicingTaskId(result.taskId);
 setAudioSlicingStatus('processing');
 
 // 轮询切片状态
 await youtubeAPI.pollSlicingStatus(result.taskId, (status) => {
 setAudioSlicingStatus(status.status);
 setAudioSlicingProgress(status.progress || 0);
 });
 
 setAudioSlicingStatus('completed');
 setAudioSlicingProgress(100);
 
 // 5秒后自动关闭进度界面
 setTimeout(() => {
 setShowAudioSlicing(false);
 }, 5000);
 
 } else {
 throw new Error(result.message || '提交切片任务失败');
 }
 } catch (error) {
 console.error('音频切片失败:', error);
 setError('音频切片失败: ' + error.message);
 setAudioSlicingStatus('failed');
 }
 };

 // 获取当前播放的片段
 const getCurrentSegment = () => {
 return textSegments.find(segment => 
 currentTime >= segment.startTime && currentTime < segment.endTime
 );
 };

 // 时间轴点击跳转
 const handleTimelineClick = (e) => {
 if (!timelineRef.current) return;
 
 const rect = timelineRef.current.getBoundingClientRect();
 const clickX = e.clientX - rect.left;
 const percentage = clickX / rect.width;
 const newTime = percentage * duration;
 
 seekTo(Math.max(0, Math.min(newTime, duration)));
 };

 // 开始编辑片段
 const startEditSegment = (segment) => {
 setEditingSegment(segment);
 setNewText(segment.text);
 setSelectedSegment(segment);
 };

 // 保存编辑的片段
 const saveEditedSegment = async () => {
 if (!editingSegment || !newText.trim()) return;

 setRegenerating(true);
 
 try {
 // 模拟重新生成音频
 await new Promise(resolve => setTimeout(resolve, 3000));
 
 // 更新片段数据
 const updatedSegments = textSegments.map(segment => 
 segment.id === editingSegment.id 
 ? { 
 ...segment, 
 text: newText, 
 isModified: true,
 audioUrl: `/mock/regenerated_${segment.id}.mp3`
 }
 : segment
 );
 
 setTextSegments(updatedSegments);
 setEditingSegment(null);
 setNewText('');
 
 } catch (err) {
 setError('重新生成音频失败');
 } finally {
 setRegenerating(false);
 }
 };

 // 取消编辑
 const cancelEdit = () => {
 setEditingSegment(null);
 setNewText('');
 };

 // 导出最终音频
 const exportFinalAudio = async () => {
 setCurrentStep(6);
 setExportProgress(0);
 
 try {
 // 模拟导出进度
 for (let i = 0; i <= 100; i += 10) {
 await new Promise(resolve => setTimeout(resolve, 200));
 setExportProgress(i);
 }
 
 // 导出完成
 setCurrentStep(7);
 
 // 模拟下载
 const link = document.createElement('a');
 link.href = '/mock/exported_podcast.mp3';
 link.download = 'podcast_final.mp3';
 link.click();
 
 } catch (err) {
 setError('导出失败，请重试');
 setCurrentStep(5);
 }
 };

 // 渲染步骤指示器
 const renderStepIndicator = () => {
 const steps = [
 { id: 1, name: '输入链接', icon: Youtube },
 { id: 2, name: '下载视频', icon: Download },
 { id: 3, name: '转录配置', icon: Settings },
 { id: 4, name: '转录处理', icon: FileText },
 { id: 5, name: '编辑音频', icon: Edit3 },
 { id: 6, name: '导出音频', icon: Music },
 { id: 7, name: '完成', icon: CheckCircle }
 ];

 return (
 <div className="mb-8">
 <div className="flex items-center justify-center">
 {steps.map((step, index) => {
 const Icon = step.icon;
 const isActive = currentStep === step.id;
 const isCompleted = currentStep > step.id;
 
 return (
 <React.Fragment key={step.id}>
 {index > 0 && (
 <div 
 className={`h-0.5 w-16 lg:w-24 transition-colors ${
 isCompleted ? 'bg-green-500' : 'bg-ink-200'
 }`}
 />
 )}
 
 <div className="relative">
 <div 
 className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
 isActive ? 'bg-accent-500 text-white ring-4 ring-accent-100' :
 isCompleted ? 'bg-green-500 text-white' :
 'bg-ink-100 text-ink-500'
 }`}
 >
 <Icon className={`w-6 h-6 ${isActive && step.id === 2 ? 'animate-spin' : ''}`} />
 </div>
 
 <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
 <span className={`text-sm font-medium ${
 isActive ? 'text-accent-600' :
 isCompleted ? 'text-green-600' :
 'text-ink-500'
 }`}>
 {step.name}
 </span>
 </div>
 </div>
 </React.Fragment>
 );
 })}
 </div>
 </div>
 );
 };

 // 渲染URL输入步骤
 const renderUrlInput = () => (
 <div className="max-w-2xl mx-auto">
 <div className="bg-cream-50 rounded-xl border p-8">
 <div className="text-center mb-6">
 <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-full mb-4">
 <Youtube className="w-8 h-8 text-accent-600" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mb-2">步骤 1：输入 YouTube 链接</h2>
 <p className="text-ink-600">将 YouTube 视频转换为可编辑的播客音频</p>
 </div>
 
 <div className="space-y-6">
 <div>
 <label className="block text-sm font-medium text-ink-700 mb-3">
 YouTube视频链接
 </label>
 <input
 type="url"
 value={youtubeUrl}
 onChange={(e) => setYoutubeUrl(e.target.value)}
 placeholder="https://www.youtube.com/watch?v=..."
 className="w-full px-4 py-4 border border-ink-300 rounded-xl focus:ring-2 focus:ring-accent-500 focus:border-transparent text-lg"
 />
 </div>
 
 <button
 onClick={handleProcessVideo}
 disabled={!youtubeUrl || processing}
 className="w-full bg-accent-500 text-white py-4 px-6 rounded-xl hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3 text-lg font-medium"
 >
 {processing ? (
 <>
 <Loader2 className="w-6 h-6 animate-spin" />
 处理中...
 </>
 ) : (
 <>
 开始处理
 <ArrowRight className="w-6 h-6" />
 </>
 )}
 </button>
 
 {error && (
 <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
 <p className="text-sm text-red-700">{error}</p>
 </div>
 )}
 </div>
 </div>
 </div>
 );

 // 处理步骤状态
 const [processingSteps, setProcessingSteps] = useState({
 download: 'pending',
 extract: 'pending',
 transcribe: 'pending'
 });

 // 渲染处理中状态
 const renderProcessing = () => {
 const steps = [
 { 
 id: 'download', 
 name: '提交下载任务', 
 icon: Youtube,
 description: '正在向服务器提交YouTube下载任务...'
 },
 { 
 id: 'extract', 
 name: '下载视频文件', 
 icon: Music,
 description: '正在从YouTube下载视频文件...'
 },
 { 
 id: 'transcribe', 
 name: '处理完成', 
 icon: FileText,
 description: '视频文件下载完成，准备进入编辑器...'
 }
 ];

 return (
 <div className="max-w-2xl mx-auto">
 <div className="bg-cream-50 rounded-xl border p-8">
 <div className="text-center mb-8">
 <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-full mb-4">
 <Loader2 className="w-8 h-8 text-accent-600 animate-spin" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mb-2">步骤 2：自动处理视频</h2>
 <p className="text-ink-600">AI 正在为您处理视频，请稍候...</p>
 </div>
 
 <div className="space-y-4">
 {steps.map((step, index) => {
 const Icon = step.icon;
 const status = processingSteps[step.id];
 
 return (
 <div key={step.id} className="relative">
 <div className={`p-5 rounded-lg border-2 transition-all ${
 status === 'completed' ? 'bg-green-50 border-green-200' :
 status === 'processing' ? 'bg-accent-50 border-accent-200' :
 'bg-cream-100 border-ink-200'
 }`}>
 <div className="flex items-center gap-4">
 <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
 status === 'completed' ? 'bg-green-500 text-white' :
 status === 'processing' ? 'bg-accent-500 text-white' :
 'bg-ink-200 text-ink-500'
 }`}>
 {status === 'completed' ? (
 <CheckCircle className="w-6 h-6" />
 ) : status === 'processing' ? (
 <Loader2 className="w-6 h-6 animate-spin" />
 ) : (
 <Icon className="w-6 h-6" />
 )}
 </div>
 
 <div className="flex-1">
 <h3 className={`font-semibold ${
 status === 'completed' ? 'text-green-800' :
 status === 'processing' ? 'text-accent-600' :
 'text-ink-600'
 }`}>
 {step.name}
 </h3>
 <p className={`text-sm mt-1 ${
 status === 'completed' ? 'text-green-600' :
 status === 'processing' ? 'text-accent-600' :
 'text-ink-500'
 }`}>
 {status === 'completed' ? '完成' :
 status === 'processing' ? step.description :
 '等待中...'}
 </p>
 </div>
 
 {status === 'processing' && (
 <div className="text-sm text-accent-600 font-medium">
 处理中...
 </div>
 )}
 </div>
 </div>
 
 {index < steps.length - 1 && (
 <div className="absolute left-5 top-full h-4 w-0.5 bg-ink-200 -translate-x-1/2"></div>
 )}
 </div>
 );
 })}
 </div>
 
 <div className="mt-6 bg-cream-100 rounded-lg p-4">
 <p className="text-sm text-ink-600 text-center">
 预计剩余时间：约 2-3 分钟
 </p>
 </div>
 </div>
 </div>
 );
 };

 // 渲染转录配置界面
 const renderTranscriptionConfig = () => {
 const languages = youtubeAPI.getSupportedLanguages();
 const formats = youtubeAPI.getSupportedFormats();
 
 return (
 <div className="max-w-2xl mx-auto">
 <div className="bg-cream-50 rounded-xl border p-8">
 <div className="text-center mb-6">
 <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-full mb-4">
 <Settings className="w-8 h-8 text-accent-600" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mb-2">步骤 3：转录配置</h2>
 <p className="text-ink-600">选择转录语言和输出格式</p>
 </div>
 
 <div className="space-y-6">
 {/* 语言选择 */}
 <div>
 <label className="block text-sm font-medium text-ink-700 mb-3">
 转录语言
 </label>
 <select
 value={transcriptionLanguage}
 onChange={(e) => setTranscriptionLanguage(e.target.value)}
 className="w-full px-4 py-3 border border-ink-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
 >
 {languages.map(lang => (
 <option key={lang.code} value={lang.code}>
 {lang.name}
 </option>
 ))}
 </select>
 </div>
 
 {/* 输出格式选择 */}
 <div>
 <label className="block text-sm font-medium text-ink-700 mb-3">
 输出格式
 </label>
 <div className="space-y-3">
 {formats.map(format => (
 <div
 key={format.code}
 className={`border rounded-lg p-4 cursor-pointer transition-all ${
 transcriptionFormat === format.code
 ? 'border-accent-500 bg-accent-50'
 : 'border-ink-200 hover:border-ink-300'
 }`}
 onClick={() => setTranscriptionFormat(format.code)}
 >
 <div className="flex items-center gap-3">
 <div className={`w-4 h-4 rounded-full border-2 ${
 transcriptionFormat === format.code
 ? 'border-accent-500 bg-accent-500'
 : 'border-ink-300'
 }`}>
 {transcriptionFormat === format.code && (
 <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
 )}
 </div>
 <div className="flex-1">
 <div className="flex items-center justify-between">
 <span className="font-medium text-ink-900">{format.name}</span>
 <span className="text-xs text-ink-500 font-mono bg-cream-100 px-2 py-1 rounded">
 {format.code}
 </span>
 </div>
 <p className="text-sm text-ink-600 mt-1">{format.description}</p>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 
 {/* 配置预览 */}
 <div className="bg-cream-100 rounded-lg p-4">
 <h3 className="text-sm font-medium text-ink-900 mb-3">配置预览</h3>
 <div className="space-y-2 text-sm">
 <div className="flex justify-between">
 <span className="text-ink-600">语言:</span>
 <span className="font-medium text-ink-900">
 {languages.find(l => l.code === transcriptionLanguage)?.name}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-ink-600">格式:</span>
 <span className="font-medium text-ink-900">
 {formats.find(f => f.code === transcriptionFormat)?.name}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-ink-600">视频链接:</span>
 <span className="font-medium text-ink-900 truncate max-w-xs">
 {downloadUrl ? '已准备' : '未准备'}
 </span>
 </div>
 </div>
 </div>
 
 <button
 onClick={handleTranscriptionConfig}
 disabled={!downloadUrl || processing}
 className="w-full bg-accent-600 text-white py-4 px-6 rounded-xl hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3 text-lg font-medium"
 >
 {processing ? (
 <>
 <Loader2 className="w-6 h-6 animate-spin" />
 开始转录...
 </>
 ) : (
 <>
 开始转录
 <ArrowRight className="w-6 h-6" />
 </>
 )}
 </button>
 
 {error && (
 <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
 <p className="text-sm text-red-700">{error}</p>
 </div>
 )}
 </div>
 </div>
 </div>
 );
 };

 // 渲染转文本处理状态
 const renderTranscription = () => (
 <div className="max-w-2xl mx-auto">
 <div className="bg-cream-50 rounded-xl border p-8">
 <div className="text-center mb-8">
 <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-full mb-4">
 <FileText className="w-8 h-8 text-accent-600" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mb-2">步骤 4：AI 转录处理</h2>
 <p className="text-ink-600">正在使用Lemonfox AI将视频转录为文本...</p>
 </div>
 
 <div className="space-y-4">
 <div className="relative">
 <div className="p-5 rounded-lg border-2 bg-accent-50 border-accent-200">
 <div className="flex items-center gap-4">
 <div className="w-10 h-10 rounded-full flex items-center justify-center bg-accent-500 text-white">
 <Loader2 className="w-6 h-6 animate-spin" />
 </div>
 
 <div className="flex-1">
 <h3 className="font-semibold text-accent-600">
 正在转录音频内容
 </h3>
 <p className="text-sm mt-1 text-accent-600">
 AI正在分析音频并生成时间戳文本片段...
 </p>
 </div>
 
 <div className="text-sm text-accent-600 font-medium">
 处理中...
 </div>
 </div>
 </div>
 </div>
 </div>
 
 <div className="mt-6 bg-cream-100 rounded-lg p-4">
 <div className="text-sm text-ink-600 space-y-2">
 <div className="flex justify-between">
 <span>转录语言:</span>
 <span className="font-medium">{youtubeAPI.getSupportedLanguages().find(l => l.code === transcriptionLanguage)?.name}</span>
 </div>
 <div className="flex justify-between">
 <span>输出格式:</span>
 <span className="font-medium">{youtubeAPI.getSupportedFormats().find(f => f.code === transcriptionFormat)?.name}</span>
 </div>
 <div className="flex justify-between">
 <span>转录任务ID:</span>
 <span className="font-medium">{transcribeJobId || '等待分配...'}</span>
 </div>
 <div className="text-center mt-3 pt-2 border-t border-ink-200">
 <span>预计处理时间：约 3-5 分钟</span>
 </div>
 </div>
 </div>
 </div>
 </div>
 );

 // 渲染音频编辑器
 const renderEditor = () => (
 <div className="max-w-7xl mx-auto space-y-6">
 {/* 步骤标题 */}
 <div className="text-center mb-6">
 <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-full mb-4">
 <Edit3 className="w-8 h-8 text-accent-600" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mb-2">步骤 5：编辑音频片段</h2>
 <p className="text-ink-600">点击任意文本片段进行编辑，AI 将重新生成对应的音频</p>
 </div>

 {/* 顶部信息栏 */}
 <div className="bg-cream-50 rounded-xl border p-6">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
 <Music className="w-6 h-6 text-accent-600" />
 </div>
 <div>
 <h2 className="text-xl font-bold text-ink-900">{audioData?.title}</h2>
 <p className="text-ink-600">总时长: {formatTime(duration)} | 共 {textSegments.length} 个片段</p>
 </div>
 </div>
 
 <div className="flex items-center gap-3">
 {/* 文本切片配置按钮 */}
 <button
 onClick={() => setShowSliceConfig(true)}
 className="bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors flex items-center gap-2 text-sm"
 >
 <Settings className="w-4 h-4" />
 文本切片设置
 </button>
 
 {/* 查看转录详情按钮 */}
 {transcriptionResult && (
 <button
 onClick={() => setShowTranscriptionDetail(true)}
 className="bg-accent-500 text-white px-4 py-2 rounded-lg hover:bg-accent-600 transition-colors flex items-center gap-2 text-sm"
 >
 <FileText className="w-4 h-4" />
 查看转录详情
 </button>
 )}
 
 <button
 onClick={exportFinalAudio}
 className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 font-medium"
 >
 <Download className="w-5 h-5" />
 进入步骤6：导出音频
 </button>
 </div>
 </div>
 </div>

 {/* 音频播放控制器 */}
 <div className="bg-cream-50 rounded-xl border p-6">
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-4">
 {/* 快退按钮 */}
 <button
 onClick={() => seekTo(Math.max(0, currentTime - 10))}
 className="w-10 h-10 bg-cream-100 text-ink-600 rounded-full flex items-center justify-center hover:bg-ink-100 transition-colors"
 title="快退10秒"
 >
 <SkipBack className="w-5 h-5" />
 </button>
 
 {/* 播放/暂停按钮 */}
 <button
 onClick={togglePlay}
 className="w-12 h-12 bg-accent-500 text-white rounded-full flex items-center justify-center hover:bg-accent-600 transition-colors shadow-lg"
 >
 {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
 </button>
 
 {/* 快进按钮 */}
 <button
 onClick={() => seekTo(Math.min(duration, currentTime + 10))}
 className="w-10 h-10 bg-cream-100 text-ink-600 rounded-full flex items-center justify-center hover:bg-ink-100 transition-colors"
 title="快进10秒"
 >
 <SkipForward className="w-5 h-5" />
 </button>
 
 <div className="flex items-center gap-2 text-sm text-ink-600 ml-4">
 <span className="font-mono">{formatTime(currentTime)}</span>
 <span>/</span>
 <span className="font-mono">{formatTime(duration)}</span>
 </div>
 </div>
 
 {/* 当前片段信息 */}
 {getCurrentSegment() && (
 <div className="flex items-center gap-3 bg-accent-50 px-4 py-2 rounded-lg">
 <AudioLines className="w-4 h-4 text-accent-600" />
 <span className="text-sm text-accent-600">
 正在播放: 片段 {textSegments.findIndex(s => s.id === getCurrentSegment().id) + 1}
 </span>
 </div>
 )}
 
 <div className="flex items-center gap-4">
 <Volume2 className="w-5 h-5 text-ink-400" />
 
 {/* 快捷键提示 */}
 <div className="hidden lg:flex items-center gap-2 text-xs text-ink-500 bg-cream-100 px-3 py-1 rounded-full">
 <Settings className="w-3 h-3" />
 <span>空格: 播放/暂停 | ←→: 快进/快退</span>
 </div>
 </div>
 </div>
 
 {/* 增强的时间轴 */}
 <div 
 className="relative bg-ink-100 rounded-full h-3 cursor-pointer hover:h-4 transition-all duration-200" 
 ref={timelineRef}
 onClick={handleTimelineClick}
 >
 {/* 进度条 */}
 <div 
 className="absolute bg-accent-500 h-full rounded-full transition-all duration-100"
 style={{ width: `${(currentTime / duration) * 100}%` }}
 />
 
 {/* 片段标记 */}
 {textSegments.map((segment, index) => {
 const isCurrentSegment = getCurrentSegment()?.id === segment.id;
 return (
 <div
 key={segment.id}
 className={`absolute top-0 h-full cursor-pointer transition-all duration-200 rounded-sm ${
 segment.isModified ? 'bg-accent-500' : 'bg-green-500'
 } ${selectedSegment?.id === segment.id ? 'ring-2 ring-accent-400 z-10' : ''} ${
 isCurrentSegment ? 'shadow-lg shadow-accent-500/50 z-20' : ''
 } hover:opacity-80`}
 style={{
 left: `${(segment.startTime / duration) * 100}%`,
 width: `${Math.max(2, ((segment.endTime - segment.startTime) / duration) * 100)}%`
 }}
 onClick={(e) => {
 e.stopPropagation();
 selectSegment(segment);
 }}
 title={`片段 ${index + 1} (${formatTime(segment.startTime)}-${formatTime(segment.endTime)}): ${segment.text.substring(0, 50)}...`}
 />
 );
 })}
 
 {/* 播放指针 */}
 <div 
 className="absolute top-0 w-0.5 h-full bg-red-500 z-30 pointer-events-none"
 style={{ left: `${(currentTime / duration) * 100}%` }}
 >
 <div className="absolute -top-1 -left-1 w-3 h-3 bg-red-500 rounded-full"></div>
 </div>
 </div>

 {/* 隐藏的音频元素 */}
 <audio 
 ref={audioRef}
 onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
 onEnded={() => setIsPlaying(false)}
 onLoadedMetadata={(e) => setDuration(e.target.duration)}
 style={{ display: 'none' }}
 />
 </div>

 {/* 文章式文本展示 */}
 <div className="bg-cream-50 rounded-xl border">
 <div className="p-6 border-b">
 <div className="flex items-center justify-between">
 <div>
 <h3 className="text-lg font-semibold text-ink-900 flex items-center gap-2">
 <FileText className="w-5 h-5" />
 转录文本 - 文章式展示
 </h3>
 <p className="text-ink-600 mt-1">点击任意段落播放对应的音频片段，双击编辑文本内容</p>
 {transcriptionFormat === 'verbose_json' && transcriptionResult && (
 <div className="mt-2 text-sm text-accent-600">
 <span className="font-medium">转录详情:</span> 
 语言 {transcriptionResult.language} | 
 总时长 {Math.floor(transcriptionResult.duration / 60)}:{(transcriptionResult.duration % 60).toFixed(1).padStart(4, '0')} | 
 共 {textSegments.length} 个片段
 </div>
 )}
 </div>
 <div className="text-sm text-ink-500">
 已编辑: {textSegments.filter(s => s.isModified).length} / {textSegments.length}
 </div>
 </div>
 </div>
 
 {/* 文章式布局：内联文本展示 */}
 <div className="p-6">
 <div className="max-w-4xl mx-auto">
 <div className="text-lg leading-relaxed">
 {textSegments.map((segment, index) => (
 <React.Fragment key={segment.id}>
 {editingSegment?.id === segment.id ? (
 // 编辑模式 - 独立编辑框
 <div className="inline-block w-full my-4 p-4 bg-cream-50 border border-ink-300 rounded-xl shadow-sm" onClick={(e) => e.stopPropagation()}>
 <textarea
 value={newText}
 onChange={(e) => setNewText(e.target.value)}
 className="w-full p-3 border border-ink-300 rounded-lg focus:ring-2 focus:ring-accent-500 resize-none"
 rows={3}
 placeholder="编辑文本内容..."
 autoFocus
 />
 
 <div className="flex items-center gap-4 mt-4">
 <div className="flex items-center gap-2">
 <User className="w-4 h-4 text-ink-500" />
 <select
 value={selectedVoice}
 onChange={(e) => setSelectedVoice(e.target.value)}
 className="px-3 py-2 border border-ink-300 rounded-lg text-sm"
 >
 <option value="female">女声</option>
 <option value="male">男声</option>
 <option value="child">童声</option>
 </select>
 </div>
 
 <div className="flex gap-2">
 <button
 onClick={saveEditedSegment}
 disabled={regenerating || !newText.trim()}
 className="bg-accent-500 text-white px-4 py-2 rounded-lg hover:bg-accent-600 disabled:opacity-50 transition-colors flex items-center gap-2 text-sm"
 >
 {regenerating ? (
 <>
 <Loader2 className="w-4 h-4 animate-spin" />
 生成中...
 </>
 ) : (
 <>
 <Save className="w-4 h-4" />
 保存
 </>
 )}
 </button>
 
 <button
 onClick={cancelEdit}
 className="bg-ink-500 text-white px-4 py-2 rounded-lg hover:bg-ink-600 transition-colors flex items-center gap-2 text-sm"
 >
 <X className="w-4 h-4" />
 取消
 </button>
 </div>
 </div>
 </div>
 ) : (
 // 显示模式 - 内联文本片段
 <span 
 className={`px-2 py-1 rounded-md transition-all duration-300 cursor-pointer ${
 selectedSegment?.id === segment.id ? 'bg-accent-200 shadow-md' : 
 getCurrentSegment()?.id === segment.id ? 'bg-green-200 shadow-md' : 
 'bg-cream-100 hover:bg-ink-100'
 }`}
 onClick={() => playSegment(segment)}
 onDoubleClick={() => startEditSegment(segment)}
 title="点击播放此片段"
 >
 {segment.text}
 </span>
 )}
 {/* 在片段之间添加一个空格，让文本自然连接 */}
 {index < textSegments.length - 1 && ' '}
 </React.Fragment>
 ))}
 </div>
 </div>
 
 {/* 底部提示 */}
 <div className="mt-6 p-4 bg-accent-50 rounded-lg border border-accent-200">
 <div className="flex items-center gap-2 text-sm text-accent-600">
 <FileText className="w-4 h-4" />
 <span className="font-medium">使用说明:</span>
 </div>
 <ul className="mt-2 text-sm text-accent-600 space-y-1">
 <li>• 点击任意文本片段播放对应的音频</li>
 <li>• 双击文本片段修改内容</li>
 <li>• 绿色背景显示当前正在播放的片段</li>
 </ul>
 </div>
 </div>
 </div>
 </div>
 );

 // 渲染导出进度
 const renderExporting = () => (
 <div className="max-w-2xl mx-auto">
 <div className="bg-cream-50 rounded-xl border p-8">
 <div className="text-center mb-8">
 <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-full mb-4">
 <Download className="w-8 h-8 text-accent-600" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mb-2">步骤 6：导出最终音频</h2>
 <p className="text-ink-600">正在合成所有片段，生成最终的播客音频...</p>
 </div>
 
 <div className="space-y-6">
 <div>
 <div className="flex justify-between mb-2">
 <span className="text-sm font-medium text-ink-700">导出进度</span>
 <span className="text-sm font-medium text-accent-600">{exportProgress}%</span>
 </div>
 <div className="w-full bg-ink-100 rounded-full h-3">
 <div 
 className="bg-accent-500 h-3 rounded-full transition-all duration-300"
 style={{ width: `${exportProgress}%` }}
 />
 </div>
 </div>
 
 <div className="bg-accent-50 rounded-lg p-4">
 <p className="text-sm text-accent-600">
 正在处理 {textSegments.filter(s => s.isModified).length} 个修改过的片段...
 </p>
 </div>
 </div>
 </div>
 </div>
 );

 // 渲染完成状态
 const renderComplete = () => (
 <div className="max-w-2xl mx-auto">
 <div className="bg-cream-50 rounded-xl border p-8">
 <div className="text-center">
 <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
 <CheckCircle className="w-10 h-10 text-green-600" />
 </div>
 <h2 className="text-3xl font-bold text-ink-900 mb-2">步骤 7：完成！</h2>
 <p className="text-ink-600 mb-8">您的YouTube视频已成功下载并处理</p>
 
 <div className="bg-cream-100 rounded-lg p-6 mb-6">
 <div className="flex items-center justify-center gap-4 mb-4">
 <Music className="w-8 h-8 text-ink-600" />
 <div className="text-left">
 <p className="font-semibold text-ink-900">YouTube视频文件</p>
 <p className="text-sm text-ink-600">时长: {formatTime(duration)} | 任务ID: {jobId}</p>
 </div>
 </div>
 
 {downloadUrl && (
 <div className="mt-4">
 <a
 href={downloadUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center gap-2 bg-accent-500 text-white px-6 py-3 rounded-lg hover:bg-accent-600 transition-colors font-medium"
 >
 <Download className="w-5 h-5" />
 下载视频文件
 </a>
 </div>
 )}
 </div>
 
 <button
 onClick={() => {
 setCurrentStep(1);
 setYoutubeUrl('');
 setTextSegments([]);
 setAudioData(null);
 setError('');
 setJobId(null);
 setDownloadUrl(null);
 setTranscribeJobId(null);
 }}
 className="bg-accent-500 text-white px-8 py-3 rounded-lg hover:bg-accent-600 transition-colors font-medium"
 >
 转换新的视频
 </button>
 </div>
 </div>
 </div>
 );

 // 渲染转录详情模态框
 const renderTranscriptionModal = () => {
 if (!showTranscriptionDetail || !transcriptionResult) return null;
 
 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-cream-50 rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden m-4">
 {/* 模态框头部 */}
 <div className="flex items-center justify-between p-6 border-b">
 <h2 className="text-xl font-bold text-ink-900">转录详情</h2>
 <button
 onClick={() => setShowTranscriptionDetail(false)}
 className="text-ink-500 hover:text-ink-700"
 >
 <X className="w-6 h-6" />
 </button>
 </div>
 
 {/* 模态框内容 */}
 <div className="p-6 overflow-y-auto max-h-[80vh]">
 {/* 转录概览 */}
 <div className="bg-cream-100 rounded-lg p-4 mb-6">
 <h3 className="font-semibold text-ink-900 mb-3">转录概览</h3>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
 <div>
 <span className="text-ink-600">任务类型:</span>
 <span className="ml-2 font-medium">{transcriptionResult.task || 'transcribe'}</span>
 </div>
 <div>
 <span className="text-ink-600">语言:</span>
 <span className="ml-2 font-medium">{transcriptionResult.language || 'zh'}</span>
 </div>
 <div>
 <span className="text-ink-600">总时长:</span>
 <span className="ml-2 font-medium">{formatTime(transcriptionResult.duration || 0)}</span>
 </div>
 <div>
 <span className="text-ink-600">输出格式:</span>
 <span className="ml-2 font-medium">{transcriptionFormat}</span>
 </div>
 </div>
 </div>
 
 {/* 完整文本 */}
 {transcriptionResult.text && (
 <div className="mb-6">
 <h3 className="font-semibold text-ink-900 mb-3">完整文本</h3>
 <div className="bg-cream-100 rounded-lg p-4 text-sm text-ink-700 leading-relaxed max-h-40 overflow-y-auto">
 {transcriptionResult.text}
 </div>
 </div>
 )}
 
 {/* 分段详情 */}
 {transcriptionResult.segments && transcriptionResult.segments.length > 0 && (
 <div>
 <h3 className="font-semibold text-ink-900 mb-3">分段详情 ({transcriptionResult.segments.length} 个片段)</h3>
 <div className="space-y-2 max-h-96 overflow-y-auto">
 {transcriptionResult.segments.map((segment) => (
 <div key={segment.id} className="border rounded-lg p-3 bg-cream-100 hover:bg-cream-100 transition-colors">
 <div className="flex items-start gap-3">
 <div className="flex-shrink-0">
 <div className="text-xs font-mono text-ink-500 bg-white px-2 py-1 rounded">
 {formatTime(segment.start)} - {formatTime(segment.end)}
 </div>
 </div>
 <div className="flex-1">
 <p className="text-sm text-ink-800 leading-relaxed">{segment.text}</p>
 {transcriptionFormat === 'verbose_json' && (
 <div className="flex items-center gap-4 mt-2 text-xs text-ink-600">
 <span>ID: {segment.id}</span>
 <span>置信度: {(Math.exp(segment.avg_logprob) * 100).toFixed(1)}%</span>
 <span>语言: {segment.language}</span>
 {segment.speaker && <span>说话人: {segment.speaker}</span>}
 </div>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 
 {/* 原始JSON数据 */}
 <div className="mt-6">
 <h3 className="font-semibold text-ink-900 mb-3">原始JSON数据</h3>
 <div className="bg-ink-900 text-green-400 rounded-lg p-4 text-xs font-mono overflow-x-auto max-h-60 overflow-y-auto">
 <pre>{JSON.stringify(transcriptionResult, null, 2)}</pre>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
 };

 // 渲染切片配置模态框
 const renderSliceConfigModal = () => {
 if (!showSliceConfig) return null;
 
 const sliceModes = [
 { value: 'original', name: '原始分段', description: '使用转录API返回的原始片段' },
 { value: 'time', name: '按时间切片', description: '按固定时间间隔重新分段' },
 { value: 'sentence', name: '按句子切片', description: '按句子数量重新分段' },
 { value: 'paragraph', name: '按段落切片', description: '根据停顿时间自动分段' },
 { value: 'custom', name: '自定义切片', description: '手动设置切分点' }
 ];
 
 const handleApplySlicing = () => {
 // 重新应用切片算法
 if (transcriptionResult && transcriptionResult.segments) {
 let processedSegments = transcriptionResult.segments.map(segment => ({
 id: segment.id + 1,
 startTime: segment.start,
 endTime: segment.end,
 text: segment.text,
 isModified: false,
 avgLogprob: segment.avg_logprob,
 language: segment.language,
 speaker: segment.speaker
 }));
 
 const slicedSegments = sliceTextSegments(processedSegments, sliceMode, sliceSettings);
 setTextSegments(slicedSegments);
 }
 
 setShowSliceConfig(false);
 };
 
 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-cream-50 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden m-4">
 <div className="flex items-center justify-between p-6 border-b">
 <h2 className="text-xl font-bold text-ink-900">文本切片设置</h2>
 <button
 onClick={() => setShowSliceConfig(false)}
 className="text-ink-500 hover:text-ink-700"
 >
 <X className="w-6 h-6" />
 </button>
 </div>
 
 <div className="p-6 overflow-y-auto max-h-[70vh]">
 <div className="space-y-6">
 {/* 切片模式选择 */}
 <div>
 <h3 className="text-lg font-semibold text-ink-900 mb-3">切片模式</h3>
 <div className="space-y-3">
 {sliceModes.map(mode => (
 <div
 key={mode.value}
 className={`border rounded-lg p-4 cursor-pointer transition-all ${
 sliceMode === mode.value
 ? 'border-accent-500 bg-accent-50'
 : 'border-ink-200 hover:border-ink-300'
 }`}
 onClick={() => setSliceMode(mode.value)}
 >
 <div className="flex items-center gap-3">
 <div className={`w-4 h-4 rounded-full border-2 ${
 sliceMode === mode.value
 ? 'border-accent-500 bg-accent-500'
 : 'border-ink-300'
 }`}>
 {sliceMode === mode.value && (
 <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
 )}
 </div>
 <div>
 <div className="font-medium text-ink-900">{mode.name}</div>
 <div className="text-sm text-ink-600">{mode.description}</div>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 
 {/* 切片参数设置 */}
 {sliceMode === 'time' && (
 <div>
 <h3 className="text-lg font-semibold text-ink-900 mb-3">时间间隔设置</h3>
 <div className="flex items-center gap-4">
 <label className="text-sm text-ink-700">每</label>
 <input
 type="number"
 value={sliceSettings.timeInterval}
 onChange={(e) => setSliceSettings({
 ...sliceSettings,
 timeInterval: parseInt(e.target.value) || 30
 })}
 className="w-20 px-3 py-2 border border-ink-300 rounded-lg text-center"
 min="10"
 max="300"
 />
 <label className="text-sm text-ink-700">秒切一段</label>
 </div>
 </div>
 )}
 
 {sliceMode === 'sentence' && (
 <div>
 <h3 className="text-lg font-semibold text-ink-900 mb-3">句子数量设置</h3>
 <div className="flex items-center gap-4">
 <label className="text-sm text-ink-700">每</label>
 <input
 type="number"
 value={sliceSettings.sentenceCount}
 onChange={(e) => setSliceSettings({
 ...sliceSettings,
 sentenceCount: parseInt(e.target.value) || 3
 })}
 className="w-20 px-3 py-2 border border-ink-300 rounded-lg text-center"
 min="1"
 max="10"
 />
 <label className="text-sm text-ink-700">句话切一段</label>
 </div>
 </div>
 )}
 
 {sliceMode === 'custom' && (
 <div>
 <h3 className="text-lg font-semibold text-ink-900 mb-3">自定义切分点</h3>
 <div className="space-y-3">
 <p className="text-sm text-ink-600">输入时间点（秒），用逗号分隔</p>
 <input
 type="text"
 value={sliceSettings.customBreaks.join(', ')}
 onChange={(e) => {
 const breaks = e.target.value
 .split(',')
 .map(s => parseFloat(s.trim()))
 .filter(n => !isNaN(n));
 setSliceSettings({
 ...sliceSettings,
 customBreaks: breaks
 });
 }}
 className="w-full px-3 py-2 border border-ink-300 rounded-lg"
 placeholder="例如: 30, 60, 120, 180"
 />
 </div>
 </div>
 )}
 
 {/* 预览信息 */}
 <div className="bg-cream-100 rounded-lg p-4">
 <h3 className="font-semibold text-ink-900 mb-2">预览</h3>
 <div className="text-sm text-ink-600">
 <p>当前模式: {sliceModes.find(m => m.value === sliceMode)?.name}</p>
 <p>原始片段: {textSegments.length} 个</p>
 {sliceMode === 'time' && <p>时间间隔: {sliceSettings.timeInterval} 秒</p>}
 {sliceMode === 'sentence' && <p>句子数量: {sliceSettings.sentenceCount} 句</p>}
 {sliceMode === 'custom' && <p>切分点: {sliceSettings.customBreaks.length} 个</p>}
 </div>
 </div>
 </div>
 </div>
 
 <div className="flex items-center justify-end gap-3 p-6 border-t">
 <button
 onClick={() => setShowSliceConfig(false)}
 className="px-4 py-2 text-ink-600 hover:text-ink-800"
 >
 取消
 </button>
 <button
 onClick={handleApplySlicing}
 className="bg-accent-600 text-white px-6 py-2 rounded-lg hover:bg-accent-700 transition-colors"
 >
 应用设置
 </button>
 </div>
 </div>
 </div>
 );
 };

 return (
 <div className="route-surface podcast-page min-h-screen bg-cream-100 py-8">
 <div className="max-w-7xl mx-auto px-4">
 {/* 步骤指示器 */}
 {renderStepIndicator()}
 
 {/* 主要内容 */}
 <div className="max-w-7xl mx-auto">
 {currentStep === 1 && renderUrlInput()}
 {currentStep === 2 && renderProcessing()}
 {currentStep === 3 && renderTranscriptionConfig()}
 {currentStep === 4 && renderTranscription()}
 {currentStep === 5 && renderEditor()}
 {currentStep === 6 && renderExporting()}
 {currentStep === 7 && renderComplete()}
 </div>
 </div>
 
 {/* 转录详情模态框 */}
 {renderTranscriptionModal()}
 
 {/* 切片配置模态框 */}
 {renderSliceConfigModal()}
 </div>
 );
}